import { celebrateLevelUp } from "../components/DashboardComponents/LevelUpCelebration"
import { isOnboardingPath } from "./routeAccess"

const celebratedLevelsKey = userId =>
  `amok.xpLevel.celebratedLevels.${userId || "anon"}`

const pendingLevelsKey = userId =>
  `amok.xpLevel.pendingCelebrations.${userId || "anon"}`

/** Backend meta.type values for XP level-up. */
export const LEVEL_UP_NOTIFICATION_META_TYPES = new Set([
  "level.up",
  "level_up",
  "xp.level_up",
  "xp.level-up",
  "user.level_up",
  "user.leveled_up",
])

const parseMaybeJson = value => {
  if (value == null) return null
  if (typeof value === "object") return value
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

const getNotificationData = source => {
  if (!source || typeof source !== "object") return {}
  if (source.data && typeof source.data === "object") return source.data
  return source
}

const getNotificationMeta = source => {
  if (!source || typeof source !== "object") return {}

  const data = getNotificationData(source)
  const candidates = [source.meta, data.meta, source.data?.meta]
  for (const candidate of candidates) {
    const parsed = parseMaybeJson(candidate)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed
    }
  }

  // FCM sometimes flattens meta onto data
  if (data.type || data.level) return data
  return {}
}

const readPendingLevels = userId => {
  try {
    const raw = JSON.parse(localStorage.getItem(pendingLevelsKey(userId)) || "[]")
    if (!Array.isArray(raw)) return []
    return raw
      .map(Number)
      .filter(n => Number.isFinite(n) && n > 0)
      .map(n => Math.floor(n))
  } catch {
    return []
  }
}

const writePendingLevels = (userId, levels) => {
  try {
    const next = [
      ...new Set(
        (Array.isArray(levels) ? levels : [])
          .map(Number)
          .filter(n => Number.isFinite(n) && n > 0)
          .map(n => Math.floor(n)),
      ),
    ].sort((a, b) => a - b)
    localStorage.setItem(pendingLevelsKey(userId), JSON.stringify(next.slice(-40)))
  } catch {
    // ignore storage failures
  }
}

export const queuePendingLevelUp = (userId, level) => {
  const nextLevel = Number(level)
  if (!Number.isFinite(nextLevel) || nextLevel <= 0) return
  if (hasCelebratedLevel(userId, nextLevel)) return
  const pending = readPendingLevels(userId)
  if (pending.includes(Math.floor(nextLevel))) return
  writePendingLevels(userId, [...pending, nextLevel])
}

const clearPendingLevel = (userId, level) => {
  writePendingLevels(
    userId,
    readPendingLevels(userId).filter(n => n !== Number(level)),
  )
}

export const hasCelebratedLevel = (userId, level) => {
  if (!level) return false
  try {
    const raw = JSON.parse(
      localStorage.getItem(celebratedLevelsKey(userId)) || "[]",
    )
    if (!Array.isArray(raw)) return false
    return raw.map(Number).includes(Number(level))
  } catch {
    return false
  }
}

export const markCelebratedLevel = (userId, level) => {
  if (!level) return
  try {
    const key = celebratedLevelsKey(userId)
    const raw = JSON.parse(localStorage.getItem(key) || "[]")
    const next = new Set(
      (Array.isArray(raw) ? raw : [])
        .map(Number)
        .filter(n => Number.isFinite(n) && n > 0),
    )
    next.add(Number(level))
    localStorage.setItem(
      key,
      JSON.stringify([...next].sort((a, b) => a - b).slice(-40)),
    )
  } catch {
    // ignore storage failures
  }
}

const readLevel = (...candidates) => {
  for (const value of candidates) {
    const level = Number(value)
    if (Number.isFinite(level) && level > 0) return Math.floor(level)
  }
  return 1
}

/**
 * Detect a level-up notification from inbox / FCM payload shapes.
 * Prefer `meta.type` (e.g. user.level_up).
 */
export const isLevelUpNotification = source => {
  if (!source || typeof source !== "object") return false

  const data = getNotificationData(source)
  const meta = getNotificationMeta(source)

  const metaType = String(source.metaType || meta.type || data.type || "")
    .trim()
    .toLowerCase()
  if (LEVEL_UP_NOTIFICATION_META_TYPES.has(metaType)) return true

  const title = String(
    source.title || data.title || source.notification?.title || "",
  ).toLowerCase()
  const message = String(
    source.message ||
      data.message ||
      data.body ||
      source.notification?.body ||
      "",
  ).toLowerCase()

  return (
    title.includes("level up") ||
    title.includes("reached level") ||
    message.includes("level up") ||
    message.includes("leveled up") ||
    message.includes("reached level")
  )
}

export const getLevelFromNotification = source => {
  if (!source || typeof source !== "object") return 1

  const data = getNotificationData(source)
  const meta = getNotificationMeta(source)

  return readLevel(
    source.level,
    meta.level,
    meta.xp_level,
    meta.new_level,
    data.level,
    data.xp_level,
    data.new_level,
    data.current_level,
  )
}

const shouldDeferLevelUpCelebration = () => {
  if (typeof window === "undefined") return false
  return isOnboardingPath(window.location.pathname)
}

/**
 * Trigger the level-up overlay once per level for this user.
 * On onboarding routes: queue only (no animation, no sound).
 */
export const maybeCelebrateLevelUpFromNotification = (
  notification,
  { userId, force = false, silent = false } = {},
) => {
  if (!isLevelUpNotification(notification)) return false

  const level = getLevelFromNotification(notification)
  if (hasCelebratedLevel(userId, level)) return false

  if (!force && shouldDeferLevelUpCelebration()) {
    queuePendingLevelUp(userId, level)
    return false
  }

  markCelebratedLevel(userId, level)
  clearPendingLevel(userId, level)
  celebrateLevelUp(level, { silent })
  return true
}

/** Scan unread inbox items; celebrate (or queue) the newest unhandled level-up. */
export const maybeCelebrateLevelUpFromInbox = (
  items = [],
  { userId, force = false, silent = false } = {},
) => {
  const list = Array.isArray(items) ? items : []
  const match = list.find(item => {
    if (!isLevelUpNotification(item)) return false
    const level = getLevelFromNotification(item)
    return !hasCelebratedLevel(userId, level)
  })
  if (!match) return false
  return maybeCelebrateLevelUpFromNotification(match, {
    userId,
    force,
    silent,
  })
}

/**
 * Play any level-ups deferred during onboarding.
 * Call once when the member reaches Dashboard.
 * Plays animation + sound together (never during onboarding).
 */
export const flushPendingLevelUpCelebrations = (userId, { items } = {}) => {
  if (shouldDeferLevelUpCelebration()) return false

  let didCelebrate = false

  // Prefer pending queue from onboarding FCM / inbox deferrals.
  const pending = readPendingLevels(userId).filter(
    level => !hasCelebratedLevel(userId, level),
  )
  if (pending.length > 0) {
    // Celebrate highest pending level (e.g. jumped to 2) once.
    // Mark all pending as celebrated so lower levels don't replay later.
    const level = Math.max(...pending)
    pending.forEach(pendingLevel => markCelebratedLevel(userId, pendingLevel))
    writePendingLevels(userId, [])
    celebrateLevelUp(level)
    didCelebrate = true
  }

  // Also pick up unread inbox level-ups that were never queued (e.g. missed FCM).
  if (!didCelebrate && Array.isArray(items) && items.length > 0) {
    const list = Array.isArray(items) ? items : []
    const match = list.find(item => {
      if (!isLevelUpNotification(item)) return false
      const level = getLevelFromNotification(item)
      return !hasCelebratedLevel(userId, level)
    })
    if (match) {
      const level = getLevelFromNotification(match)
      if (level && !hasCelebratedLevel(userId, level)) {
        markCelebratedLevel(userId, level)
        clearPendingLevel(userId, level)
        celebrateLevelUp(level)
        didCelebrate = true
      }
    }
  }

  return didCelebrate
}
